import { create } from 'zustand';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  type User,
} from 'firebase/auth';
import { auth, skipAuth } from '../lib/firebase';
import { setTokenProvider, apiGet, apiPost } from '../lib/api-client';

// Minimal stand-in so the rest of the app can treat `user` as truthy.
const DEV_USER = { uid: 'dev-user', email: 'dev@localhost' } as User;

const LOCAL_TOKEN_KEY = 'rr_auth_token';

/** True when Firebase is NOT configured — use local auth instead */
const useLocalAuth = skipAuth;

interface AuthState {
  user: User | null;
  role: string;
  loading: boolean;
  error: string | null;

  init: () => void;
  fetchRole: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  role: 'user',
  loading: true,
  error: null,

  fetchRole: async () => {
    try {
      const data = await apiGet<{ role: string }>('/api/me');
      set({ role: data.role });
    } catch { /* ignore */ }
  },

  init: () => {
    if (useLocalAuth) {
      // Local auth: check for stored token
      const token = localStorage.getItem(LOCAL_TOKEN_KEY);
      if (token) {
        setTokenProvider(async () => token);
        // Verify token is still valid by hitting /api/me
        apiGet<{ uid: string; email: string; role: string }>('/api/me')
          .then((data) => {
            const localUser = { uid: data.uid, email: data.email } as User;
            set({ user: localUser, role: data.role, loading: false });
          })
          .catch(() => {
            localStorage.removeItem(LOCAL_TOKEN_KEY);
            set({ user: null, loading: false });
          });
      } else {
        set({ loading: false });
      }
      return;
    }

    // Wire up the token provider for the API client (Firebase)
    setTokenProvider(async () => {
      const user = auth!.currentUser;
      if (!user) return null;
      return user.getIdToken();
    });

    // Listen to auth state changes
    onAuthStateChanged(auth!, (user) => {
      set({ user, loading: false });
      if (user) useAuthStore.getState().fetchRole();
    });
  },

  signIn: async (email, password) => {
    set({ error: null, loading: true });

    if (useLocalAuth) {
      try {
        const data = await apiPost<{ token: string; user: { uid: string; email: string; displayName: string; role: string } }>('/api/auth/login', { email, password });
        localStorage.setItem(LOCAL_TOKEN_KEY, data.token);
        setTokenProvider(async () => data.token);
        set({ user: { uid: data.user.uid, email: data.user.email } as User, role: data.user.role, loading: false });
      } catch (err: unknown) {
        set({ error: (err as Error).message, loading: false });
      }
      return;
    }

    try {
      await signInWithEmailAndPassword(auth!, email, password);
    } catch (err: unknown) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  signUp: async (email, password) => {
    set({ error: null, loading: true });

    if (useLocalAuth) {
      try {
        const data = await apiPost<{ token: string; user: { uid: string; email: string; displayName: string; role: string } }>('/api/auth/register', { email, password });
        localStorage.setItem(LOCAL_TOKEN_KEY, data.token);
        setTokenProvider(async () => data.token);
        set({ user: { uid: data.user.uid, email: data.user.email } as User, role: data.user.role, loading: false });
      } catch (err: unknown) {
        set({ error: (err as Error).message, loading: false });
      }
      return;
    }

    try {
      await createUserWithEmailAndPassword(auth!, email, password);
    } catch (err: unknown) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  signInWithGoogle: async () => {
    if (useLocalAuth) return;
    set({ error: null, loading: true });
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth!, provider);
    } catch (err: unknown) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  signOut: async () => {
    if (useLocalAuth) {
      localStorage.removeItem(LOCAL_TOKEN_KEY);
      set({ user: null, role: 'user' });
      return;
    }
    await firebaseSignOut(auth!);
    set({ user: null, role: 'user' });
  },

  clearError: () => set({ error: null }),
}));
